import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Image } from 'src/components/common/Image';

import {
  googleUniversalEvent,
  tikTokAdvancedMatching,
  tikTokEvent,
  useAnalytics,
} from '@features/Analytics';
import {
  createAppLovinEcommercePayload,
  getAppLovinActualPrice,
} from '@features/Analytics/utils';
import { useFeature } from '@features/FeatureToggle';
import { PAYMENT_MODAL_QUERY_PARAM } from '@features/Paywall';
import { FormattedMessage } from '@features/intl';

import ShieldIcon from '../../../../assets/icons/shield-check.svg';
import AmexLogo from '../../../../assets/images/payment/amex-logo.svg';
import ApplePayLogo from '../../../../assets/images/payment/apple-pay-logo.svg';
import BrowserMenuArrow from '../../../../assets/images/payment/browser-menu-arrow.svg';
import PaymentLoader from '../../../../assets/images/payment/loader.svg';
import MastercardLogo from '../../../../assets/images/payment/mastercard-logo.svg';
import UnionpayLogo from '../../../../assets/images/payment/unionpay-logo.svg';
import VisaLogo from '../../../../assets/images/payment/visa-logo.svg';
import CloseButton from '../../../../components/common/close-button/close-button';
import Alert from '../../../common/modal/alert/alert';
import Modal from '../../../common/modal/modal/modal';
import { useStores } from '../../../common/root-store-provider/root-store-provider';
import ApplePayPaymentButton, {
  SHOW_APPLE_PAY_BUTTON,
  useApplePayPaymentButton,
} from '../apple-pay-payment-button/apple-pay-payment-button';
import CardPaymentButton from '../card-payment-button/card-payment-button';
import PaypalPaymentButton from '../paypal-payment-button/paypal-payment-button';
import styles from './payment-modal.module.scss';

export enum PaymentModalStatus {
  SHOW = '1',
  HIDE = '0',
}

const PaymentModal = observer(
  ({
    openApplePayCheckoutOnMount,
  }: {
    openApplePayCheckoutOnMount: boolean;
  }) => {
    const { trackFacebookEvent, trackGoogleEvent, trackAppLovin } =
      useAnalytics();
    const fbPixelSept2025Feature = useFeature('fbPixelSept2025Feature');
    const {
      paymentsStore,
      authStore: { user },
    } = useStores();
    const router = useRouter();
    const { query, pathname } = router;
    const { allSubscriptionPlans, setWasPaymentModalClosed } = paymentsStore;

    const isPaymentModalActive =
      query[PAYMENT_MODAL_QUERY_PARAM] === PaymentModalStatus.SHOW;
    const selectedPlanId = query['plan'];

    const onHide = () => {
      // noinspection JSIgnoredPromiseFromCall
      router.push(
        {
          pathname,
          query: {
            ...query,
            [PAYMENT_MODAL_QUERY_PARAM]: PaymentModalStatus.HIDE,
          },
        },
        undefined,
        { shallow: true },
      );
      trackGoogleEvent({
        eventName: 'initiate_checkout_skipped',
      });
    };

    // Selected subscription plan

    const subscriptionPlan = useMemo(
      () =>
        selectedPlanId
          ? allSubscriptionPlans?.find(({ id }) => id === selectedPlanId)
          : undefined,
      [allSubscriptionPlans, selectedPlanId],
    );

    useEffect(() => {
      if (isPaymentModalActive && subscriptionPlan) {
        !fbPixelSept2025Feature &&
          trackFacebookEvent({
            eventName: 'InitiateCheckout',
            customData: {
              paymentSystem: 'braintree',
            },
            options: {
              email: user?.email,
            },
          });
        googleUniversalEvent({
          action: 'initiate_checkout',
          category: 'get_plan',
          label: 'InitiateCheckout',
          value: subscriptionPlan.fullPriceDiscount,
        });
        user?.email ? tikTokAdvancedMatching(user.email) : null;
        tikTokEvent('InitiateCheckout');
        const appLovinActualPrice = subscriptionPlan.hasTrial
          ? subscriptionPlan.fractionalPriceDiscount
          : getAppLovinActualPrice({
              fullPrice: subscriptionPlan.fullPrice,
              fullPriceDiscount: subscriptionPlan.fullPriceDiscount,
              introOfferData: subscriptionPlan.introOfferData,
              isIntroOfferEnabled: Boolean(subscriptionPlan.introOfferData),
            });
        trackAppLovin({
          eventName: 'begin_checkout',
          options: createAppLovinEcommercePayload({
            currency: subscriptionPlan.currency,
            value: appLovinActualPrice,
            itemId: subscriptionPlan.braintreePlanId ?? subscriptionPlan.id,
            itemName: subscriptionPlan.name,
            userEmail: user?.email,
            userId: user?.serverId,
          }),
        });
      }
    }, [
      fbPixelSept2025Feature,
      isPaymentModalActive,
      subscriptionPlan,
      trackAppLovin,
      trackFacebookEvent,
      user?.email,
      user?.serverId,
    ]);

    // Apple Pay

    const { showSafariRedirectPrompt } = useApplePayPaymentButton();

    //render

    const [isPaypalLoading, setPaypalLoading] = useState(true);
    const [isApplePayLoading, setApplePayLoading] = useState(true);
    const onPaypalLoadEnd = useCallback(() => setPaypalLoading(false), []);
    const onApplePayLoadEnd = useCallback(() => setApplePayLoading(false), []);

    const isLoading =
      isPaypalLoading || (SHOW_APPLE_PAY_BUTTON && isApplePayLoading);

    if (!subscriptionPlan) {
      return <div className={styles.hideLoading}></div>;
    }

    const isPlanHasPriceIntroOffer =
      subscriptionPlan && +subscriptionPlan.priceAfterIntroOffer > 0;

    return (
      <>
        <Alert
          hidden={showSafariRedirectPrompt}
          modalContentClassName={styles.container}
          title="Select your
          payment method"
          footer={
            <footer className={styles.footer}>
              <p>
                <FormattedMessage
                  defaultMessage="Guaranteed Safe Checkout"
                  id="Onboarding.Paywall.Main.SafeCheckout"
                />
              </p>
              <figure className={styles.logosContainer}>
                <Image
                  className={styles.paypalLogo}
                  src="paypal-logo.webp"
                  width={23}
                  height={23}
                  alt=""
                />
                <VisaLogo className={styles.visaLogo} />
                <MastercardLogo className={styles.mastercardLogo} />
                <AmexLogo className={styles.amexLogo} />
                <UnionpayLogo className={styles.unionpayLogo} />
              </figure>
              <p>
                <ShieldIcon />
                <FormattedMessage
                  defaultMessage="All transactions are secure and encrypted"
                  id="Onboarding.Paywall.Main.Encrypted"
                />
              </p>
            </footer>
          }
          show={isPaymentModalActive}
          onHide={() => {
            onHide();
            setApplePayLoading(true);
            setPaypalLoading(true);
            setWasPaymentModalClosed(true);
          }}
        >
          <hr className={styles.line} />
          <CloseButton
            className={styles.headerCloseButton}
            onClick={() => {
              onHide();
              setApplePayLoading(true);
              setPaypalLoading(true);
              setWasPaymentModalClosed(true);
            }}
          />
          <div
            className={classNames(
              styles.paymentButtonsContainer,
              isLoading && styles.hidePayments,
            )}
          >
            <div className={styles.currentPlanInfo}>
              <div className={styles.planPriceBlock}>
                <span>
                  {subscriptionPlan.billingPeriod}{' '}
                  {subscriptionPlan.billingPeriodUnit} subscription
                </span>
                <span>
                  {isPlanHasPriceIntroOffer
                    ? subscriptionPlan.priceAfterIntroOffer
                    : subscriptionPlan.fullPrice}{' '}
                  {subscriptionPlan.currency}
                </span>
              </div>
              {subscriptionPlan.discountPercentage !== 0 &&
                (isPlanHasPriceIntroOffer ? (
                  <div className={styles.planDiscountBlock}>
                    <span>
                      {Math.round(
                        ((+subscriptionPlan.priceAfterIntroOffer -
                          subscriptionPlan.fullPriceDiscount) /
                          +subscriptionPlan.priceAfterIntroOffer) *
                          100,
                      )}
                      % intro offer discount
                    </span>
                    <span>
                      -
                      {Number(
                        +subscriptionPlan.priceAfterIntroOffer -
                          subscriptionPlan.fullPriceDiscount,
                      ).toFixed(2)}{' '}
                      {subscriptionPlan.currency}
                    </span>
                  </div>
                ) : (
                  <div className={styles.planDiscountBlock}>
                    <span>
                      {Math.round(
                        ((+subscriptionPlan.fullPrice -
                          subscriptionPlan.fullPriceDiscount) /
                          +subscriptionPlan.fullPrice) *
                          100,
                      )}
                      % personal discount
                    </span>
                    <span>
                      -
                      {Number(
                        +subscriptionPlan.fullPrice -
                          subscriptionPlan.fullPriceDiscount,
                      ).toFixed(2)}{' '}
                      {subscriptionPlan.currency}
                    </span>
                  </div>
                ))}
              <hr className={styles.currentPlanInfoLine} />
              <div className={styles.totalBlock}>
                <em>Total:</em>
                <span>
                  <em>
                    {subscriptionPlan.fullPriceDiscount.toFixed(2)}{' '}
                    {subscriptionPlan.currency}
                  </em>{' '}
                  for {subscriptionPlan.billingPeriod}{' '}
                  {subscriptionPlan.billingPeriodUnit}
                </span>
              </div>
            </div>
            {SHOW_APPLE_PAY_BUTTON && (
              <ApplePayPaymentButton
                className={styles.paymentButton}
                subscriptionPlan={subscriptionPlan}
                openCheckoutOnMount={openApplePayCheckoutOnMount}
                onLoadEnd={onApplePayLoadEnd}
              />
            )}
            <PaypalPaymentButton
              className={styles.paymentButton}
              subscriptionPlan={subscriptionPlan}
              onLoadEnd={onPaypalLoadEnd}
            />
            <CardPaymentButton
              className={styles.paymentButton}
              subscriptionPlan={subscriptionPlan}
            />
            {isPlanHasPriceIntroOffer && (
              <div className={styles.disclaimer}>
                You agree that ${subscriptionPlan.fullPriceDiscount} will be
                charged as an intro offer, and, then $
                {subscriptionPlan.priceAfterIntroOffer} will be automatically
                billed every {subscriptionPlan.billingPeriod}{' '}
                {subscriptionPlan.billingPeriodUnit}. Cancel anytime.
              </div>
            )}
          </div>
          {isLoading && (
            <div className={styles.loaderContainer}>
              <PaymentLoader className={styles.loader} />
              Loading payment options...
            </div>
          )}
          <div id="test" />
        </Alert>
        {showSafariRedirectPrompt && (
          <Modal show={showSafariRedirectPrompt} canClose={false}>
            <div className={styles.safariRedirectPrompt}>
              <figure className={styles.safariRedirectPromptGraphic}>
                <BrowserMenuArrow />
                <figcaption>Open in Safari</figcaption>
              </figure>
              <div className={styles.safariRedirectPromptDetails}>
                <h2>
                  <ApplePayLogo /> is only available
                  <br /> on the Safari web browser
                </h2>
                <p>
                  To use Apple Pay, tap ••• and open this page in the Safari
                  browser
                </p>
              </div>
              <p className={styles.safariRedirectPromptBackHint}>
                You can turn back and choose another payment method
              </p>
            </div>
          </Modal>
        )}
      </>
    );
  },
);

export default PaymentModal;
